import { Params } from '@angular/router';
import { documentRoute, documentSource, DocumentLabels } from './documents.calc';
import { matchesText, policyTitle, vehicleName } from './panel.calc';
import { CaseService, CaseSummary, Financing, Owner, PanelDocument, Policy, TicketSummary, Vehicle } from './panel.models';

/**
 * Wyszukiwarka w pasku (Issue 14): w danych klienta, które panel już ma (pojazdy, polisy, umowy, sprawy,
 * dokumenty) — zakres klienta zapewnia API tych list. Od 2 znaków, maks. 4 wyniki w grupie; grupy w kolejności
 * z prototypu. Każdy wynik prowadzi jednym kliknięciem do właściwej strony (dokument sprawy — do etapu).
 */

export const SEARCH_MIN = 2;
export const SEARCH_GROUP_SIZE = 4;

export type SearchGroupCode = 'vehicles' | 'policies' | 'financing' | 'cases' | 'tickets' | 'documents';

export interface SearchItem {
    title: string;
    subtitle: string;
    link: (string | number)[];
    queryParams: Params | null;
    /** Firma pozycji — etykieta na koncie z firmami powiązanymi (Issue 26). */
    owner: Owner | null;
}

export interface SearchGroup {
    code: SearchGroupCode;
    items: SearchItem[];
}

export interface SearchData {
    vehicles: Vehicle[];
    policies: Policy[];
    financing: Financing[];
    cases: CaseSummary[];
    /** Zgłoszenia (Issue 40) — po temacie i numerze („Z-1042”). */
    tickets?: TicketSummary[];
    documents: PanelDocument[];
}

export interface SearchLabels extends DocumentLabels {
    caseTitle: (id: number, title: string) => string;
    services: (services: CaseService[], renewal: boolean) => string;
    policyNumber: (number: string) => string;
    contractNumber: (number: string) => string;
    contractPreparing: string;
    /** Data pliku w podpisie wyniku („wczoraj, 15:15”). */
    date: (value: string) => string;
}

export function search(query: string, data: SearchData, labels: SearchLabels): SearchGroup[] {
    if (query.trim().length < SEARCH_MIN) {
        return [];
    }

    const vehicle = (id: string | null) => data.vehicles.find((v) => v.id === id) ?? null;
    const join = (...parts: (string | null | undefined)[]) => parts.filter(Boolean).join(' · ');

    const vehicles = data.vehicles
        .filter((v) => matchesText(query, v.mark, v.model, v.version, v.registrationNumber, v.vin))
        .map((v) => ({
            title: vehicleName(v),
            subtitle: join(v.registrationNumber, v.vin),
            link: ['/pojazdy', v.id],
            queryParams: null,
            owner: v.owner ?? null,
        }));

    const policies = data.policies
        .filter((p) => {
            const v = vehicle(p.vehicleId);

            return matchesText(query, p.insurer, p.scopeLabel, p.type, p.number, v?.mark, v?.model, v?.registrationNumber);
        })
        .map((p) => ({
            title: policyTitle(p),
            subtitle: join(p.number ? labels.policyNumber(p.number) : null, vehicleName(vehicle(p.vehicleId))),
            link: ['/polisy', p.id],
            queryParams: null,
            owner: p.owner ?? null,
        }));

    const financing = data.financing
        .filter((f) => {
            const v = vehicle(f.vehicleId);

            return matchesText(query, f.financingInstitution, f.financingType, f.contractNumber, v?.mark, v?.model, v?.registrationNumber);
        })
        .map((f) => ({
            title: f.financingInstitution ?? f.financingType ?? labels.contractPreparing,
            subtitle: join(f.contractNumber ? labels.contractNumber(f.contractNumber) : labels.contractPreparing, vehicleName(vehicle(f.vehicleId))),
            link: ['/finansowanie', f.id],
            queryParams: null,
            owner: f.owner ?? null,
        }));

    const cases = data.cases
        .filter((c) => matchesText(query, c.title, c.subtitle, `#${c.id}`))
        .map((c) => ({
            title: labels.caseTitle(c.id, c.title),
            subtitle: labels.services(c.services, c.renewal),
            link: ['/sprawy', c.id],
            queryParams: c.clientAction ? { etap: c.clientAction.stageCode } : null,
            owner: c.owner ?? null,
        }));

    const tickets = (data.tickets ?? [])
        .filter((t) => matchesText(query, t.title, t.number, t.categoryLabel))
        .map((t) => ({
            title: t.title,
            subtitle: join(t.number, t.categoryLabel),
            link: ['/zgloszenia', t.id],
            queryParams: null,
            owner: t.owner ?? null,
        }));

    const documents = data.documents
        .map((d) => ({ doc: d, source: documentSource(d, data, labels) }))
        .filter(({ doc, source }) => matchesText(query, doc.name, doc.number, source))
        .map(({ doc, source }) => {
            const route = documentRoute(doc);

            return { title: doc.name, subtitle: join(source, doc.date ? labels.date(doc.date) : null), link: route.link, queryParams: route.queryParams, owner: doc.owner ?? null };
        });

    const groups: SearchGroup[] = [
        { code: 'vehicles', items: vehicles },
        { code: 'policies', items: policies },
        { code: 'financing', items: financing },
        { code: 'cases', items: cases },
        { code: 'tickets', items: tickets },
        { code: 'documents', items: documents },
    ];

    return groups.filter((g) => g.items.length).map((g) => ({ ...g, items: g.items.slice(0, SEARCH_GROUP_SIZE) }));
}
